import { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import CustomerLayout from "../components/CustomerLayout";
import api from "../lib/api";
import { toast } from "sonner";
import { Key, Download, Copy, Trash2, Plus, RotateCcw } from "lucide-react";

const API_BASE_URL = "https://api.root-seo.com/api";

const ApiAccessPage = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newToken, setNewToken] = useState(null);
  const [maxKeys, setMaxKeys] = useState(3);
  const [resettingId, setResettingId] = useState(null);

  const makeKeyName = () => {
    const raw = (user?.name || user?.email || "user").toString();
    const base = raw
      .split("@")[0]
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 24) || "user";
    const suffix = Math.random().toString(36).slice(2, 8);
    return `${base}-${suffix}`;
  };

  const load = async () => {
    try {
      const res = await api.get("/me/api-keys");
      setKeys(res.data.items || []);
      setMaxKeys(res.data.max_keys || 3);
    } catch (e) {
      toast.error(e.response?.data?.detail || "API key listesi alınamadı");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createKey = async () => {
    setCreating(true);
    try {
      const res = await api.post("/me/api-keys", { name: makeKeyName() });
      setNewToken(res.data.token);
      toast.success(language === "tr" ? "API key oluşturuldu — şimdi kopyalayın!" : "API key created — copy it now!");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Oluşturulamadı");
    } finally {
      setCreating(false);
    }
  };

  const removeKey = async (id) => {
    if (!confirm(language === "tr" ? "Bu key kalıcı olarak silinsin mi?" : "Permanently delete this key?")) return;
    try {
      await api.delete(`/me/api-keys/${id}`);
      toast.success(language === "tr" ? "Silindi" : "Deleted");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === "tr" ? "Silinemedi" : "Delete failed"));
    }
  };

  const resetIp = async (id) => {
    const msg = language === "tr"
      ? "IP kilidi sıfırlansın mı? Sonraki API isteği hangi sunucudan gelirse key o IP'ye kilitlenecek."
      : "Reset IP lock? The next API request will bind the key to that server's IP.";
    if (!confirm(msg)) return;
    setResettingId(id);
    try {
      await api.post(`/me/api-keys/${id}/reset-ip`);
      toast.success(language === "tr" ? "IP kilidi sıfırlandı" : "IP lock reset");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === "tr" ? "Sıfırlanamadı" : "Reset failed"));
    } finally {
      setResettingId(null);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(language === "tr" ? "Kopyalandı" : "Copied");
  };

  return (
    <CustomerLayout activeId="api">
      <div className="w-full max-w-3xl mx-auto px-4 sm:px-6 md:px-8 py-6 md:py-10">
        <div className="mb-8 text-center">
          <h1 className="text-2xl md:text-3xl font-mono font-bold mb-2 flex items-center justify-center gap-3">
            <Key className="w-7 h-7 text-primary flex-shrink-0" />
            API
          </h1>
          <p className="text-muted-foreground font-mono text-sm max-w-xl mx-auto">
            {language === "tr"
              ? "Kendi sisteminizden link basımı için API key oluşturun. 1 basım = $15 (max 3 keyword)."
              : "Create API keys for link pushes from your system. 1 push = $15 (max 3 keywords)."}
          </p>
        </div>

        {newToken && (
          <div className="mb-6 p-4 border border-amber-500/40 bg-amber-500/10">
            <div className="text-amber-300 text-xs font-mono mb-2 text-center">
              {language === "tr"
                ? "TOKEN YALNIZCA BİR KEZ GÖSTERİLİR — HEMEN KOPYALAYIN"
                : "TOKEN SHOWN ONCE — COPY NOW"}
            </div>
            <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
              <code className="flex-1 text-sm break-all bg-black/50 p-2 font-mono">{newToken}</code>
              <div className="flex gap-2 justify-center">
                <Button size="sm" onClick={() => copy(newToken)} className="font-mono">
                  <Copy className="w-4 h-4 mr-1" />
                  {language === "tr" ? "Kopyala" : "Copy"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setNewToken(null)} className="font-mono">
                  OK
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="glass p-5 sm:p-6 mb-6 text-center">
          <h2 className="text-sm font-mono uppercase text-muted-foreground mb-4">
            {language === "tr" ? "Yeni API Key" : "New API Key"}
          </h2>
          <Button
            onClick={createKey}
            disabled={creating || keys.filter((k) => k.is_active).length >= maxKeys}
            className="font-mono bg-primary text-black rounded-none w-full sm:w-auto min-w-[200px]"
          >
            <Plus className="w-4 h-4 mr-1" />
            {creating
              ? (language === "tr" ? "OLUŞTURULUYOR…" : "CREATING…")
              : (language === "tr" ? "OLUŞTUR" : "CREATE")}
          </Button>
          <p className="text-[11px] text-muted-foreground mt-3 font-mono">
            {language === "tr" ? `Aktif limit: ${maxKeys} key` : `Active limit: ${maxKeys} keys`}
          </p>
        </div>

        <div className="glass mb-6 overflow-hidden">
          <div className="px-5 py-3 border-b border-white/10 text-xs font-mono uppercase text-muted-foreground text-center">
            {language === "tr" ? "API Anahtarları" : "API Keys"}
          </div>
          {loading ? (
            <div className="p-6 text-center text-muted-foreground font-mono text-sm">
              {language === "tr" ? "Yükleniyor..." : "Loading..."}
            </div>
          ) : keys.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground font-mono text-sm">
              {language === "tr" ? "Henüz key yok" : "No keys yet"}
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {keys.map((k) => (
                <div key={k.id} className="px-4 sm:px-5 py-4 space-y-3">
                  <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-sm">
                    <div className="flex-1 min-w-[140px]">
                      <div className="font-mono font-bold truncate">{k.name}</div>
                      <div className="text-xs text-muted-foreground font-mono break-all">{k.token_preview}</div>
                    </div>
                    <div className="text-xs text-muted-foreground font-mono text-right">
                      <div>{k.requests_count} req</div>
                      <div>{k.orders_count} push</div>
                    </div>
                    <span className="text-[10px] font-mono px-2 py-0.5 bg-emerald-500/20 text-emerald-400">
                      AKTİF
                    </span>
                    <Button size="icon" variant="ghost" className="text-red-400" onClick={() => removeKey(k.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 text-xs font-mono">
                    <div className="flex-1 text-muted-foreground">
                      {k.bound_ip ? (
                        <span>
                          {language === "tr" ? "Kilitli IP:" : "Bound IP:"}{" "}
                          <span className="text-foreground">{k.bound_ip}</span>
                        </span>
                      ) : (
                        <span className="text-amber-400/90">
                          {language === "tr"
                            ? "Henüz IP'ye kilitlenmedi — ilk istekten sonra kilitlenir"
                            : "Not bound yet — locks on first request"}
                        </span>
                      )}
                      {k.blocked_count > 0 && (
                        <span className="block sm:inline sm:ml-3 text-red-400/90 mt-1 sm:mt-0">
                          {language === "tr"
                            ? `${k.blocked_count} engellenen istek`
                            : `${k.blocked_count} blocked requests`}
                          {k.last_blocked_ip ? ` (${k.last_blocked_ip})` : ""}
                        </span>
                      )}
                    </div>
                    {k.bound_ip && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={resettingId === k.id}
                        onClick={() => resetIp(k.id)}
                        className="font-mono shrink-0"
                      >
                        <RotateCcw className="w-3.5 h-3.5 mr-1" />
                        {resettingId === k.id
                          ? (language === "tr" ? "SIFIRLANIYOR…" : "RESETTING…")
                          : (language === "tr" ? "IP KİLİDİNİ SIFIRLA" : "RESET IP LOCK")}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="glass p-5 sm:p-6 mb-6 font-mono text-sm">
          <h2 className="text-xs uppercase text-muted-foreground mb-3 text-center">
            {language === "tr" ? "API Base URL" : "API Base URL"}
          </h2>
          <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center justify-center">
            <code className="flex-1 text-center sm:text-left break-all bg-black/40 px-3 py-2 text-primary">
              {API_BASE_URL}
            </code>
            <Button size="sm" variant="outline" onClick={() => copy(API_BASE_URL)} className="font-mono shrink-0">
              <Copy className="w-4 h-4 mr-1" />
              {language === "tr" ? "Kopyala" : "Copy"}
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3 text-center leading-relaxed">
            {language === "tr"
              ? "Auth: X-API-Key veya Authorization: Bearer (rseo_u_…). Örnek: GET /customer-api/v1/health"
              : "Auth: X-API-Key or Authorization: Bearer (rseo_u_…). Example: GET /customer-api/v1/health"}
          </p>
        </div>

        <div className="flex flex-col items-center">
          <a
            href="/docs/customer-api.md"
            download="root-seo-customer-api.md"
            className="inline-flex items-center justify-center gap-2 px-4 py-3 border border-primary/40 text-primary font-mono text-sm hover:bg-primary/10 transition-colors w-full sm:w-auto"
          >
            <Download className="w-4 h-4" />
            {language === "tr" ? "API DOKÜMANI İNDİR" : "DOWNLOAD API DOCS"}
          </a>
        </div>
      </div>
    </CustomerLayout>
  );
};

export default ApiAccessPage;
